import {
	ComponentProps,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { useLocalize, useStreamAgent } from '@treams/treams-hooks';
import { useReadableColor } from '@treams/treams-hooks/src/useReadableColor';
import {
	AlertIcon,
	CompassIcon,
	ConversationIcon,
	FriendsIcon,
	GiftIcon,
	PuzzleIcon,
	SailboatIcon,
	SearchIcon,
	SuitcaseIcon,
	ViewIcon,
} from '@treams/treams-icons';
import { NoteTemplateIcon, NoteTemplatePartialFragment } from '@treams/treams-network';
import {
	Card,
	FlexGap,
	Heading3,
	IconWrapper,
	Paragraph,
	Skeleton,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import { CommentComposerHandle } from '../../../comments/components/CommentComposer';

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: ${tokens.gap.list}px ${tokens.gap.group}px;
`;

const TEMPLATE_ICON_MAP: Record<NoteTemplateIcon, React.ReactNode> = {
	[NoteTemplateIcon.View]: <ViewIcon />,
	[NoteTemplateIcon.Conversation]: <ConversationIcon />,
	[NoteTemplateIcon.Compass]: <CompassIcon />,
	[NoteTemplateIcon.Puzzle]: <PuzzleIcon />,
	[NoteTemplateIcon.Suitcase]: <SuitcaseIcon />,
	[NoteTemplateIcon.Friends]: <FriendsIcon />,
	[NoteTemplateIcon.Alert]: <AlertIcon />,
	[NoteTemplateIcon.Gift]: <GiftIcon />,
	[NoteTemplateIcon.Sailboat]: <SailboatIcon />,
	[NoteTemplateIcon.Search]: <SearchIcon />,
};

const TemplateGrid = Skeleton(
	function TemplateGrid(props: {
		noteID: string;
		subjectUserID: string;
		templates: NoteTemplatePartialFragment[];
		composerRef: RefObject<CommentComposerHandle | null>;
		onInsightReceived?: () => void;
	}) {
		const { t } = useTranslation();
		const { stream, text } = useStreamAgent();
		const { localize, language } = useLocalize();
		const abortControllerRef = useRef<AbortController | null>(null);

		const templateOptions = useMemo(() => {
			return [...props.templates]
				.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
				.map(({ id, title, subtitle, icon, iconColor }) => ({
					id,
					title: localize(title).text,
					subtitle: localize(subtitle).text,
					color: iconColor ?? 'black',
					icon: TEMPLATE_ICON_MAP[icon ?? NoteTemplateIcon.View],
				}));
		}, [props.templates, localize]);

		useEffect(() => {
			return () => abortControllerRef.current?.abort();
		}, []);

		const onTemplateClick = useCallback(
			async (noteTemplateID: string) => {
				abortControllerRef.current?.abort();
				const controller = new AbortController();
				abortControllerRef.current = controller;

				props.composerRef.current?.setInsight('');

				try {
					await stream(
						'TALKING_POINTS',
						{
							subjectUserID: props.subjectUserID,
							noteTemplateID,
							noteID: props.noteID,
							locale: language.id,
						},
						controller.signal,
					);
				} catch (err) {
					if (err instanceof DOMException && err.name === 'AbortError') return;
					throw err;
				}

				if (controller.signal.aborted) return;

				// Notify parent only after the stream has fully completed so a parent
				// re-render (e.g. hiding the templates) can't unmount us mid-stream and
				// abort the fetch.
				props.onInsightReceived?.();
				props.composerRef.current?.setMessage(t('oneOnOnes.templates.successMessage'));
			},
			[
				props.subjectUserID,
				props.noteID,
				language.id,
				stream,
				props.composerRef,
				props.onInsightReceived,
				t,
			],
		);

		useEffect(() => {
			if (!text) return;

			props.composerRef.current?.setInsight(text);
		}, [text, props.composerRef]);

		return (
			<Grid>
				{templateOptions.map(({ id, ...option }, index) => (
					<TemplateCard key={index} {...option} onClick={() => onTemplateClick(id)} />
				))}
			</Grid>
		);
	},
	function CardGridSkeleton() {
		return (
			<Grid>
				<Skeleton.Repeat count={6}>
					<TemplateCard.Skeleton />
				</Skeleton.Repeat>
			</Grid>
		);
	},
);

export default TemplateGrid;

const templateCardProps: Partial<ComponentProps<typeof Card>> = {
	p: tokens.padding.control,
};

const TemplateCard = Skeleton(
	function TemplateCard(
		props: { title: string; subtitle: string; onClick: () => Promise<unknown> } & ComponentProps<
			typeof TemplateIcon
		>,
	) {
		const { title, subtitle, ...iconProps } = props;
		const [loading, setLoading] = useState(false);

		const onClick = async () => {
			setLoading(true);
			await props.onClick();
			setLoading(false);
		};

		return (
			<Card {...templateCardProps} onClick={onClick}>
				<FlexGap mb={2} justifyContent="space-between">
					<TemplateIcon {...iconProps} />
					{loading && <Spinner />}
				</FlexGap>
				<Heading3>{title}</Heading3>
				<Paragraph>{subtitle}</Paragraph>
			</Card>
		);
	},
	function TemplateCardSkeleton() {
		return (
			<Card.Skeleton {...templateCardProps}>
				<FlexGap gap={8} flexDirection="column">
					<Circle $background={tokens.surface.skeleton} $foreground={tokens.icon.secondary}>
						<Skeleton.Box width="100%" height="100%" />
					</Circle>
					<Heading3.Skeleton />
					<Paragraph.Skeleton lines={0.5} />
				</FlexGap>
			</Card.Skeleton>
		);
	},
);

const TEMPLATE_ICON_COLORS: Record<string, { background: string; foreground: string }> = {
	primary: { background: tokens.brand.background, foreground: tokens.brand.iconOnBg },
	primaryVibrant: { background: tokens.brand.background, foreground: tokens.brand.icon },
	primaryLight: { background: tokens.brand.background, foreground: tokens.brand.iconOnBg },
	green: { background: tokens.success.background, foreground: tokens.success.iconOnBg },
	yellow: { background: tokens.warning.background, foreground: tokens.warning.iconOnBg },
	orange: { background: tokens.warning.background, foreground: tokens.warning.iconOnBg },
	danger: { background: tokens.error.background, foreground: tokens.error.iconOnBg },
	red: { background: tokens.error.background, foreground: tokens.error.iconOnBg },
	black: { background: tokens.surface.secondary, foreground: tokens.icon.primary },
	antracite: { background: tokens.surface.secondary, foreground: tokens.icon.primary },
	slateBlue: { background: tokens.surface.secondary, foreground: tokens.icon.secondary },
	lightBlue: { background: tokens.info.background, foreground: tokens.info.iconOnBg },
	blue: { background: tokens.info.background, foreground: tokens.info.iconOnBg },
};

const Circle = styled.div<{ $background: string; $foreground: string }>`
	width: 32px;
	height: 32px;
	border-radius: ${tokens.radius.full}px;
	background-color: ${(props) => props.$background};
	color: ${(props) => props.$foreground};
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	svg {
		width: 16px;
		height: 16px;
		display: block;
		color: inherit;
	}

	svg path:not([fill='none']) {
		fill: currentColor;
	}

	svg [stroke] {
		stroke: currentColor;
	}
`;

function TemplateIcon(props: { color: string; icon: React.ReactNode }) {
	const theme = useTheme();
	const mapped = TEMPLATE_ICON_COLORS[props.color];
	const background =
		mapped?.background ??
		(props.color in theme.colors
			? String(theme.colors[props.color as keyof typeof theme.colors])
			: props.color);
	const readableForeground = useReadableColor(background, {
		preference: { mode: 'light', contrastRatio: 1.5 },
	});
	const foreground = mapped?.foreground ?? readableForeground;

	return (
		<Circle $background={background} $foreground={foreground}>
			<IconWrapper size={16} color={foreground}>
				{props.icon}
			</IconWrapper>
		</Circle>
	);
}
